import { useEffect, useState } from "react";
import { Trash2, Plus, X, AlertTriangle } from "lucide-react";
import client from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";

/**
 * ProjectManagerModal — Create / edit / delete projects.
 * Props:
 *   open, onOpenChange       — control the modal
 *   mode ("create"|"edit")   — form flavor
 *   project (edit mode)      — the project doc being edited
 *   onCreated / onUpdated / onDeleted — callbacks
 */
export function ProjectManagerModal({ open, onOpenChange, mode, project, onCreated, onUpdated, onDeleted }) {
    const isEdit = mode === "edit";
    const [name, setName] = useState("");
    const [domain, setDomain] = useState("");
    const [location, setLocation] = useState("United States");
    const [competitors, setCompetitors] = useState("");   // one per line
    const [starterKw, setStarterKw] = useState("");        // one per line
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!open) {
            setName("");
            setDomain("");
            setLocation("United States");
            setCompetitors("");
            setStarterKw("");
            setConfirmDelete(false);
            setSaving(false);
            setDeleting(false);
            return;
        }
        if (isEdit && project) {
            setName(project.name || "");
            setDomain(project.domain || "");
            setLocation(project.location || "United States");
            setCompetitors((project.competitors || []).join("\n"));
        }
    }, [open, isEdit, project]);

    const parseLines = (blob) => (blob || "")
        .split("\n")
        .map((s) => s.trim().replace(/^https?:\/\//i, "").replace(/\/+$/, ""))
        .filter(Boolean);

    const submit = async () => {
        if (!name.trim() || !domain.trim()) {
            toast.error("Name and domain are required");
            return;
        }
        setSaving(true);
        try {
            const cleanedDomain = domain.trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");
            const comps = parseLines(competitors);
            const payload = {
                name: name.trim(),
                domain: cleanedDomain,
                competitors: comps,
                location: location.trim() || "United States",
            };
            let created;
            if (isEdit) {
                const r = await client.patch(`/projects/${project.id}`, payload);
                created = r.data;
                toast.success(`"${created.name}" updated`);
                onUpdated?.(created);
            } else {
                const kws = parseLines(starterKw);
                if (kws.length) payload.starter_keywords = kws;
                const r = await client.post("/projects", payload);
                created = r.data;
                toast.success(`Project "${created.name}" created`);
                onCreated?.(created);
            }
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || (isEdit ? "Update failed" : "Create failed"));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setDeleting(true);
        try {
            await client.delete(`/projects/${project.id}`);
            toast.success(`"${project.name}" deleted`);
            onDeleted?.(project);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="project-modal" className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">
                        {isEdit ? "Edit project" : "New project"}
                    </DialogTitle>
                    <DialogDescription>
                        {isEdit
                            ? "Rename, change the tracked domain, or update your competitor set."
                            : "Track a new website's keywords, backlinks, and site health."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1 uppercase tracking-widest">Name</label>
                        <input
                            data-testid="project-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Acme Marketing"
                            className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1 uppercase tracking-widest">Domain</label>
                        <input
                            data-testid="project-domain-input"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                            placeholder="acme.com"
                            className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                            Just the hostname — we strip http(s):// automatically.
                        </p>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1 uppercase tracking-widest">Location</label>
                        <select
                            data-testid="project-location-input"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                        >
                            {[
                                "United States", "United Kingdom", "Canada", "Australia",
                                "India", "Germany", "France", "Spain", "Brazil", "Mexico",
                                "Japan", "Singapore", "United Arab Emirates",
                            ].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1 uppercase tracking-widest">
                            Competitor domains
                        </label>
                        <textarea
                            data-testid="project-competitors-input"
                            value={competitors}
                            onChange={(e) => setCompetitors(e.target.value)}
                            rows={3}
                            placeholder={"competitor-a.com\ncompetitor-b.com"}
                            className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary resize-none"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1 font-mono">One per line. Leave blank if unsure.</p>
                    </div>
                    {!isEdit && (
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1 uppercase tracking-widest">
                                Starter keywords <span className="normal-case text-muted-foreground/70">· optional</span>
                            </label>
                            <textarea
                                data-testid="project-starter-keywords-input"
                                value={starterKw}
                                onChange={(e) => setStarterKw(e.target.value)}
                                rows={3}
                                placeholder={"organic vitamin c\nplant-based protein\nvegan skincare"}
                                className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary resize-none"
                            />
                            <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                                One per line. If empty we'll seed you with 13 popular SEO keywords.
                            </p>
                        </div>
                    )}
                </div>

                {isEdit && (
                    <div className="border-t border-border pt-4 mt-2">
                        {!confirmDelete ? (
                            <button
                                data-testid="delete-project-btn"
                                onClick={() => setConfirmDelete(true)}
                                className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 font-mono"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete this project
                            </button>
                        ) : (
                            <div className="border border-red-500/40 bg-red-500/5 p-3" data-testid="delete-confirm-panel">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <div className="text-xs font-medium">Delete "{project?.name}"?</div>
                                        <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                                            This also deletes every keyword, audit, backlink, brief, share link, and disavow export tied to this project. Irreversible.
                                        </div>
                                        <div className="flex items-center gap-2 mt-3">
                                            <button
                                                data-testid="delete-confirm-btn"
                                                onClick={remove}
                                                disabled={deleting}
                                                className="bg-red-500 text-white px-3 py-1.5 text-xs hover:bg-red-600 disabled:opacity-50"
                                            >
                                                {deleting ? "Deleting…" : "Yes, delete forever"}
                                            </button>
                                            <button
                                                data-testid="delete-cancel-btn"
                                                onClick={() => setConfirmDelete(false)}
                                                className="text-xs text-muted-foreground hover:text-foreground px-3 py-1.5"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <button
                        data-testid="project-modal-cancel"
                        onClick={() => onOpenChange(false)}
                        className="text-sm text-muted-foreground hover:text-foreground px-4 py-2"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="project-modal-submit"
                        onClick={submit}
                        disabled={saving}
                        className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
                    >
                        {isEdit ? "Save changes" : (<><Plus className="w-4 h-4" /> Create project</>)}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
